import { useWriteDexSdk } from "@/contexts/write-dex-sdk-context";

import { useDecibelWallet } from "./use-decibel-wallet";
import { useGetImportedSessionKey } from "./use-imported-session-keys";

export type AuthState = "LOADING" | "WALLET_CONNECTED" | "SESSION_KEY_IMPORTED" | "DISCONNECTED";

export const useAuthState = () => {
  const {
    connected,
    accountAddress,
    keylessInfo,
    isLoading: isLoadingWallet,
    ownerWalletAddress: walletOwnerAddress,
    chainName,
    isEVM,
  } = useDecibelWallet();
  const { writeDexSdk, isLoading: isLoadingWriteDexSdk, subaccountAddress } = useWriteDexSdk();
  const importedSessionKey = useGetImportedSessionKey(subaccountAddress);

  // User has connected their wallet and has an active subaccount
  // This is the primary authenticated state where the user controls their own wallet
  if (connected && subaccountAddress && accountAddress) {
    return {
      state: "WALLET_CONNECTED" as const,
      subaccountAddress,
      accountAddress,
      keylessInfo,
      isWalletConnected: connected,
      // Delegated to the provider so there is one definition of "owner address": it is the only
      // one that resolves Sui, where the fallback below would hand back the derived Aptos address.
      ownerWalletAddress: walletOwnerAddress ?? accountAddress,
      chainName,
      isEVM,
    };
  }

  // User is using an imported session key (API wallet) without connecting their own wallet
  // This allows trading with pre-authorized session keys from external sources
  if (writeDexSdk && subaccountAddress && importedSessionKey)
    return {
      state: "SESSION_KEY_IMPORTED" as const,
      subaccountAddress,
      accountAddress: importedSessionKey.accountAddress,
      isWalletConnected: false,
      ownerWalletAddress: importedSessionKey.ownerAddress,
      chainName,
      isEVM: false,
    };

  if (isLoadingWallet || isLoadingWriteDexSdk) {
    return {
      state: "LOADING" as const,
      isWalletConnected: false,
      accountAddress: undefined,
      subaccountAddress: undefined,
      ownerWalletAddress: undefined,
      chainName,
      isEVM: false,
    };
  }

  return {
    state: "DISCONNECTED" as const,
    accountAddress: undefined,
    subaccountAddress: undefined,
    isWalletConnected: false,
    ownerWalletAddress: undefined,
    chainName,
    isEVM: false,
  };
};
